import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import Paragraph from "../Paragraph";
import './SplitContent.scss';
import {paragraphObjectBackground} from "../../helpers/graphQLShortcuts";
import {fieldObjectSectionStyleCss} from "../../helpers/createStyles";
import {filterHeadline, filterHTML} from "../../helpers/filters";

interface Data {
    paragraphById: {
        fieldTxtHeadline: string;
        fieldHtmlBody: Array<{
            value: string;
            format: string;
            processed: string;
        }>;
        fieldCssId: string;
        fieldVarAdditionalCss: string;
        fieldVarFullWidth: string;
        fieldHideImageOnMobile: string;
        fieldVarSplitColumns: string;
        fieldVarContainerPadding: string;
        fieldVarMobileDisplayOrder: string;
        fieldInnerSplitItems: Array<{
            targetRevisionId: string
        }>;
        fieldVarComponentBackground: Array<{}>;
    }
}

class SplitContentQuery extends Query<Data, {id: string}> {}

const GET_COMPONENT = gql`
query paragraphById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
      id,
    ... on Paragraph {
      ... on Paragraph {
        ... on ParagraphComponentSplitContent {
          fieldTxtHeadline
          fieldHtmlBody {
            value
            format
            processed
          }
          fieldCssId
          fieldVarAdditionalCss
          fieldVarFullWidth
          fieldHideImageOnMobile
          fieldVarSplitColumns
          fieldVarContainerPadding
          fieldVarMobileDisplayOrder
          fieldInnerSplitItems {
            targetRevisionId
          }
          fieldVarComponentBackground {
              entity {
                ${paragraphObjectBackground}
              }
          }
        }
      }
    }
  }
}
`;

const SplitContent = ({id, markComponentLoadedAs, handleLoadedComponents}) => (
    <SplitContentQuery asyncMode query={GET_COMPONENT} variables={{ id }}>
        {({ loading, error, data }) => {
            if (loading) return null;
            if (error) {
                markComponentLoadedAs(id, true);
                return <div>{error}</div>;
            }

            const field = data.paragraphById;

            // Ensure 2 inner items exist before rendering
            if (!field.fieldInnerSplitItems || field.fieldInnerSplitItems.length < 2) return null;

            let classes = '';

            if (field.fieldVarFullWidth) {
                classes += ' splitContent--fullWidth';
            }
            if (field.fieldVarSplitColumns) {
                classes += ` splitContent--columns-${field.fieldVarSplitColumns}`;
            }
            if (field.fieldVarMobileDisplayOrder === 'primary_first') {
                classes += ` splitContent--primaryFirstMobile`
            }
            if (field.fieldVarContainerPadding) {
                classes += ` splitContent--padding-${field.fieldVarContainerPadding}`;
            }
            if (field.fieldHideImageOnMobile) {
                classes += ' splitContent--hideImageMobile';
            }

            if (field.fieldVarAdditionalCss) {
                classes += ` ${field.fieldVarAdditionalCss}`;
            }

            // Generate Styles Object
            const styles = fieldObjectSectionStyleCss({
                entity: {
                    fieldObjectBackground: field.fieldVarComponentBackground
                }
            }, classes.trim());

            const hasHeaderAndBody = (field.fieldTxtHeadline || (field.fieldHtmlBody && field.fieldHtmlBody.processed)) && (field.fieldInnerSplitItems && field.fieldInnerSplitItems.length);

            markComponentLoadedAs(id, true);

            return (
                <React.Fragment>
                    {field.fieldTxtHeadline || (field.fieldHtmlBody && field.fieldHtmlBody.processed) ? (
                        <div className={`splitContentHeader${hasHeaderAndBody ? ' minimal-bottom-padding hasContent' : ''}`}>
                            {styles.childBackground.isActive && (
                                <div style={styles.childBackground.css}/>
                            )}

                            <div style={{position: 'relative'}} className={!field.fieldVarFullWidth ? 'container' : ''}>
                                {field.fieldTxtHeadline && (
                                    // eslint-disable-next-line react/no-danger
                                    <h2 dangerouslySetInnerHTML={{__html: filterHeadline(field.fieldTxtHeadline)}} />
                                )}
                                {field.fieldHtmlBody && field.fieldHtmlBody.processed && (
                                    // eslint-disable-next-line react/no-danger
                                    <div dangerouslySetInnerHTML={{__html: filterHTML(field.fieldHtmlBody.processed)}}/>
                                )}
                            </div>
                        </div>
                    ) : null}

                    <div className={`splitContent${styles.classNames}${hasHeaderAndBody ? ' no-top-padding-desktop hasContent' : ''}`} style={styles.css} id={field.fieldCssId || null}>
                        {styles.childBackground.isActive && (
                            <div style={styles.childBackground.css}/>
                        )}

                        {!field.fieldVarFullWidth ? (
                            <div className="container">
                                {field.fieldInnerSplitItems.map(item => (
                                    <Paragraph key={`${id}_${item.targetRevisionId}`}
                                               id={String(item.targetRevisionId)}
                                               handleLoadedComponents={handleLoadedComponents}/>
                                ))}
                            </div>
                        ) : field.fieldInnerSplitItems.map(item => (
                            <Paragraph key={`${id}_${item.targetRevisionId}`}
                                       id={String(item.targetRevisionId)}
                                       handleLoadedComponents={handleLoadedComponents}/>
                        ))}

                    </div>
                </React.Fragment>
            );
        }}
    </SplitContentQuery>
);

SplitContent.propTypes = {
    id: PropTypes.string.isRequired,
    markComponentLoadedAs: PropTypes.func.isRequired,
    handleLoadedComponents: PropTypes.func.isRequired
};

export default SplitContent;



// WEBPACK FOOTER //
// ./frontend/components/SplitContent/SplitContent.jsx